/**
 * DebugBadge — top-left overlay showing version + build time + environment +
 * audio diagnostics.
 *
 * Visibility rules:
 *   - DEV (vite dev server): always visible.
 *   - PROD or staging build: visible only when `?debug=1` is in the URL
 *     OR when `localStorage.sai_debug === '1'` is set.
 *
 * Click the main badge → copies version info to clipboard.
 *
 * Audio diagnostics block (added 2026-05-24 after JB reported "all
 * still silent"):
 *   - ♪ Howler test — fires playSound('tap_ding') and shows a count.
 *     If count goes up but you hear nothing, the issue is downstream
 *     of Howler (system mute, output device, browser audio policy).
 *   - 🔊 Native test — a bare <audio controls> pointing at the same
 *     mp3. If the native player works but Howler doesn't, the issue
 *     is Howler init / autoplay unlock. If neither works, it's the
 *     OS / browser output.
 *   - AudioContext state — shows running/suspended/closed so we know
 *     whether the browser ever unlocked the audio stack.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Howl, Howler } from 'howler';
import { resetAllStateAndReload } from '../debug/resetAll.js';
import { getActiveSounds, playSound, type ActiveSoundSnapshot } from '../sounds/player.js';
import { useSoundSettings } from '../sounds/settings.js';
import { getTelemetryBuffer } from '../telemetry/emit.js';
import { getLiveFps } from '../telemetry/fpsMonitor.js';
import styles from './DebugBadge.module.css';

/** Tier labels for the per-tier debug panel. */
const TIER_LABELS: readonly { tier: 0 | 1 | 2 | 3 | 4 | 5; label: string }[] = [
  { tier: 0, label: 'bed' },
  { tier: 1, label: 'breath' },
  { tier: 2, label: 'feedback' },
  { tier: 3, label: 'event' },
  { tier: 4, label: 'peak' },
  { tier: 5, label: 'voice' },
];

function shouldShow(): boolean {
  if (import.meta.env.DEV) return true;
  if (typeof window === 'undefined') return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get('debug') === '1') return true;
  try {
    if (window.localStorage.getItem('sai_debug') === '1') return true;
  } catch {
    // localStorage may be blocked in some iframes / privacy modes — ignore.
  }
  return false;
}

function detectEnv(): 'dev' | 'staging' | 'prod' {
  if (import.meta.env.DEV) return 'dev';
  const flag = (import.meta.env.VITE_APP_ENV as string | undefined) ?? '';
  if (flag === 'staging') return 'staging';
  return 'prod';
}

/** Format ISO timestamp as a short, local-tz `MMM-DD HH:mm` for the badge. */
function formatBuildTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const month = d.toLocaleString('en', { month: 'short' });
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${month}-${day} ${hh}:${mm}`;
}

/** Snapshot of the diagnostics block — refreshed on every test fire. */
interface AudioDiag {
  readonly ctxState: string;
  readonly playedCount: number;
  readonly failedCount: number;
  readonly lastFail: string | null;
  readonly howlerGlobalVolume: number;
  readonly usingWebAudio: boolean;
  readonly effectiveTapDingVolume: number;
}

function readDiag(): AudioDiag {
  // Howler.ctx is the shared Web Audio context (may be null in
  // HTML5-Audio-only fallback path).
  const H = Howler as unknown as {
    ctx?: AudioContext | null;
    usingWebAudio?: boolean;
    volume: () => number;
  };
  const ctx = H.ctx;
  const ctxState = ctx?.state ?? '(no ctx — HTML5 fallback)';
  const buf = getTelemetryBuffer();
  const played = buf.filter((r) => r.name === 'sound.played').length;
  const failed = buf.filter((r) => r.name === 'sound.failed');
  const settings = useSoundSettings.getState();
  // Pull a sample definition to verify the effective volume math.
  const tapDef = { variants: [], volume: 1.0, category: 'ui' as const, tier: 2 as const };
  return {
    ctxState,
    playedCount: played,
    failedCount: failed.length,
    lastFail: failed.length > 0 ? JSON.stringify(failed[failed.length - 1]?.payload) : null,
    howlerGlobalVolume: H.volume(),
    usingWebAudio: H.usingWebAudio ?? false,
    effectiveTapDingVolume: settings.effectiveVolume(tapDef),
  };
}

export function DebugBadge(): JSX.Element | null {
  const [visible, setVisible] = useState<boolean>(() => shouldShow());
  const [copied, setCopied] = useState<boolean>(false);
  const [diag, setDiag] = useState<AudioDiag>(() => readDiag());
  const [activeSounds, setActiveSounds] = useState<readonly ActiveSoundSnapshot[]>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  // 2026-05-26 perf debug: live FPS отображается в badge — JB видит цифру
  // на экране mobile сразу, без открытия DevTools.
  const [liveFps, setLiveFps] = useState<{ fps: number; p95FrameMs: number }>({
    fps: 60,
    p95FrameMs: 16.67,
  });
  // JB 2026-05-26: «дебага должна быть возможность свернуть панель».
  // Свёрнутая панель = маленький pill в углу с версией. Развернутая = всё.
  // Persist в localStorage чтобы при reload состояние сохранялось.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('sai_debug_collapsed') === '1';
    } catch {
      return false;
    }
  });
  const env = useMemo<'dev' | 'staging' | 'prod'>(() => detectEnv(), []);
  const buildTimeShort = useMemo(() => formatBuildTime(__BUILD_TIME__), []);
  const tierMuted = useSoundSettings((s) => s.tierMuted);
  const setTierMuted = useSoundSettings((s) => s.setTierMuted);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('sai_debug_collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Poll diagnostics 4x/sec while visible — cheap, only on dev/debug UI.
  useEffect(() => {
    if (!visible) return;
    const id = window.setInterval(() => {
      setDiag(readDiag());
      setActiveSounds(getActiveSounds());
      setLiveFps(getLiveFps());
    }, 250);
    return () => {
      window.clearInterval(id);
    };
  }, [visible]);

  // Re-evaluate visibility on URL change (e.g. user typed ?debug=1).
  useEffect(() => {
    const handler = (): void => {
      setVisible(shouldShow());
    };
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, []);

  const handleCopy = useCallback(async (): Promise<void> => {
    const text = [
      `version: ${__APP_VERSION__}`,
      `built:   ${__BUILD_TIME__}`,
      `env:     ${env}`,
      `url:     ${window.location.href}`,
      `ua:      ${navigator.userAgent}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      // Some browsers block clipboard writes outside user gesture; fail silently.
    }
  }, [env]);

  if (!visible) return null;

  const envClassName =
    env === 'dev' ? styles.envDev : env === 'staging' ? styles.envStaging : styles.envProd;
  const wrapperClass = styles.badge ?? '';
  const envChipClass = `${styles.envChip ?? ''} ${envClassName ?? ''}`;
  const versionClass = styles.version ?? '';
  const buildTimeClass = styles.buildTime ?? '';

  const audioBtnClass = styles.audioBtn ?? '';

  // Collapsed mode — tiny pill that just shows env + version + live FPS.
  // 2026-05-26: FPS indicator — green ≥50, amber 30-49, red <30. Видно
  // сразу прямо в углу TG bot, не открывая дебаг-панель.
  if (collapsed) {
    const fpsColor = liveFps.fps >= 50 ? '#7bc97b' : liveFps.fps >= 30 ? '#d6b94c' : '#e07b7b';
    return (
      <button
        type="button"
        className={`${wrapperClass} ${styles.collapsed ?? ''}`}
        onClick={toggleCollapsed}
        aria-label="Развернуть дебаг-панель"
        title={`Развернуть · FPS ${liveFps.fps.toString()}, p95 ${liveFps.p95FrameMs.toString()}ms`}
      >
        <span className={envChipClass}>{env}</span>{' '}
        <span className={versionClass}>{__APP_VERSION__}</span>{' '}
        <span style={{ color: fpsColor, fontWeight: 600 }}>{liveFps.fps.toString()}fps</span>
      </button>
    );
  }

  return (
    <div className={wrapperClass} role="status" aria-label={`Версия ${__APP_VERSION__} · ${env}`}>
      <button
        type="button"
        className={styles.collapseBtn ?? ''}
        onClick={(e) => {
          e.stopPropagation();
          toggleCollapsed();
        }}
        title="Свернуть дебаг-панель"
        aria-label="Свернуть дебаг-панель"
      >
        ▴
      </button>
      <div
        onClick={() => {
          void handleCopy();
        }}
        title="Tap to copy version info"
        style={{ cursor: 'pointer' }}
      >
        <span className={envChipClass}>{env}</span>{' '}
        <span className={versionClass}>{__APP_VERSION__}</span>
      </div>
      <div className={buildTimeClass}>{buildTimeShort}</div>

      {/* === Audio diagnostics block === */}
      <button
        type="button"
        className={audioBtnClass}
        onClick={(e) => {
          e.stopPropagation();
          const ctx = (Howler as unknown as { ctx?: AudioContext | null }).ctx;
          if (ctx?.state === 'suspended') {
            void ctx.resume();
          }
          playSound('tap_ding');
        }}
        title="Test via Howler default (WebAudio path)"
      >
        ♪ howler webaudio ({diag.playedCount.toString()})
      </button>

      {/* Howler in forced HTML5-Audio mode. This bypasses the WebAudio
          path entirely and uses a real <audio> element under the hood.
          If THIS plays but the WebAudio button above doesn't, the bug
          is in Howler's WebAudio routing (gain node lost, master node
          disconnected, etc). Confirmed 2026-05-24: native <audio> plays,
          Howler webaudio doesn't. */}
      <button
        type="button"
        className={audioBtnClass}
        onClick={(e) => {
          e.stopPropagation();
          const h = new Howl({
            src: ['/sounds/ui/tap_ding/v01.mp3'],
            html5: true,
            volume: 1.0,
            preload: true,
          });
          h.play();
        }}
        title="Test via Howler in HTML5 mode — bypasses WebAudio"
      >
        ♪ howler html5
      </button>

      {/* Bare native HTML5 audio — third reference point. */}
      <audio
        controls
        preload="none"
        src="/sounds/ui/tap_ding/v01.mp3"
        className={styles.nativeAudio ?? ''}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />

      <div className={buildTimeClass}>
        ctx: {diag.ctxState} · webAudio: {diag.usingWebAudio ? 'yes' : 'no'}
      </div>
      <div className={buildTimeClass}>
        H.vol: {diag.howlerGlobalVolume.toFixed(2)} · eff: {diag.effectiveTapDingVolume.toFixed(2)}
        {diag.failedCount > 0 ? ` · ✗${diag.failedCount.toString()}` : ''}
      </div>
      {diag.lastFail !== null && (
        <div className={buildTimeClass} style={{ color: '#e07a6a' }}>
          {diag.lastFail.slice(0, 60)}
        </div>
      )}

      {/* === Per-tier sound mute + active sounds (JB 2026-05-25 debug tool) === */}
      <button
        type="button"
        className={audioBtnClass}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        title="Развернуть звуковую панель"
      >
        ▾ {expanded ? 'свернуть' : 'звуки по слоям'}
      </button>
      {expanded && (
        <div className={styles.tierPanel ?? ''}>
          {TIER_LABELS.map(({ tier, label }) => {
            const activeOnTier = activeSounds.filter((s) => s.tier === tier);
            const muted = tierMuted[tier];
            return (
              <div key={tier} className={styles.tierRow ?? ''}>
                <button
                  type="button"
                  className={`${styles.tierMute ?? ''} ${muted ? (styles.tierMuteOn ?? '') : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTierMuted(tier, !muted);
                  }}
                  title={`${muted ? 'размутить' : 'замутить'} tier ${tier.toString()} (${label})`}
                >
                  {muted ? '🔇' : '🔊'}
                </button>
                <span className={styles.tierLabel ?? ''}>
                  T{tier} {label}
                </span>
                <span className={styles.tierActive ?? ''}>
                  {activeOnTier.length === 0 ? '·' : activeOnTier.map((s) => s.eventId).join(', ')}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* === Reset all state — JB admin tool === */}
      <button
        type="button"
        className={`${audioBtnClass} ${styles.resetBtn ?? ''}`}
        onClick={(e) => {
          e.stopPropagation();
          // eslint-disable-next-line no-alert -- admin debug tool, не shipped игроку
          const confirmed = window.confirm(
            'Сбросить ВСЁ — прогресс, артефакты, онбординг — и перезагрузить страницу? (Настройки звука сохранятся.)',
          );
          if (confirmed) {
            resetAllStateAndReload();
          }
        }}
        title="Сброс всего persistent state до состояния «впервые открыл апку»"
      >
        ⟲ сбросить всё
      </button>

      {copied && <div className={styles.copied}>copied</div>}
    </div>
  );
}
